import { get, putApi } from '../api';
import { isActiveSession } from '../utils/verification';
import { clearCart, replaceCart } from './reducers/cartSlice';
import { store } from './store';

/**
 * Keeps the local cart in sync with the server-saved cart
 * (GET/PUT /api/sales/cart/), so web and mobile sessions share one cart.
 *
 * Policy (deliberate, keep in sync with mobile-app):
 * - On login (and on app start while logged in) the server cart REPLACES
 *   the local one — no merging.
 * - On logout the local cart is cleared.
 * - While logged in, cart mutations are pushed as a debounced full-cart
 *   PUT (idempotent replace; rapid +/- clicks collapse into one call).
 * - Logged-out carts stay local-only and are discarded at login.
 */

const DEBOUNCE_MS = 1200;

let pushTimer = null;
// JSON payload last known to match the server — used to skip no-op pushes
// (e.g. the state change caused by replaceCart itself).
let lastSyncedPayload = null;
let started = false;

const isLoggedIn = (state) => isActiveSession(state.user.user);

const payloadFor = (cartItems) =>
  cartItems.map((item) => ({
    inventory_item: item.id,
    quantity: item.cartQuantity,
  }));

// Server line -> the same shape addToCart stores (inventory item + cartQuantity).
const serverLineToCartItem = (line) => ({
  ...line.inventory_item_details,
  cartQuantity: line.quantity,
});

const pullServerCart = async () => {
  const { data } = await get('sales/cart/');
  const items = (data.items || []).map(serverLineToCartItem);
  lastSyncedPayload = JSON.stringify(payloadFor(items));
  store.dispatch(replaceCart(items));
};

const pushServerCart = async () => {
  const state = store.getState();
  if (!isLoggedIn(state)) return;
  const payload = JSON.stringify(payloadFor(state.cart.cartItems));
  if (payload === lastSyncedPayload) return;
  try {
    await putApi('sales/cart/', { items: JSON.parse(payload) });
    lastSyncedPayload = payload;
  } catch (error) {
    // Leave lastSyncedPayload stale so the next mutation retries.
    console.warn('Cart sync push failed', error);
  }
};

const schedulePush = () => {
  clearTimeout(pushTimer);
  pushTimer = setTimeout(pushServerCart, DEBOUNCE_MS);
};

const flushPush = () => {
  clearTimeout(pushTimer);
  pushTimer = null;
  pushServerCart();
};

export const startCartSync = () => {
  if (started) return;
  started = true;

  let prevUser = store.getState().user.user;
  let prevCartItems = store.getState().cart.cartItems;

  store.subscribe(() => {
    const state = store.getState();
    const { user } = state.user;
    const { cartItems } = state.cart;

    if (user !== prevUser) {
      const wasLoggedIn = isActiveSession(prevUser);
      const nowLoggedIn = isActiveSession(user);
      prevUser = user;
      if (!wasLoggedIn && nowLoggedIn) {
        pullServerCart().catch((error) =>
          console.warn('Cart sync pull failed', error)
        );
      } else if (wasLoggedIn && !nowLoggedIn) {
        clearTimeout(pushTimer);
        pushTimer = null;
        lastSyncedPayload = null;
        store.dispatch(clearCart());
      }
    }

    if (cartItems !== prevCartItems) {
      prevCartItems = cartItems;
      if (isLoggedIn(state)) {
        schedulePush();
      }
    }
  });

  // Session already logged in on page load: server cart is the truth.
  if (isLoggedIn(store.getState())) {
    pullServerCart().catch((error) =>
      console.warn('Cart sync pull failed', error)
    );
  }

  // Best-effort flush so a pending debounced push isn't lost on tab close.
  window.addEventListener('pagehide', flushPush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPush();
  });
};
